import React, { useEffect, useRef, useState, useMemo } from "react";
import { Message } from "../../../services/chatService/ChatService";
import { groupMessagesByDate } from "../../../utilities/chat/groupingUtils";
import MessageItem from "./MessageItem";
import { ValidateOtpResponseDto } from "../../../interfaces/ExtermalUserDtos/ExternalUsersDtos";
import CallLogMessage from "./callLogMessages/CallLogMessage";

const getUTCTime = (dateValue: string | Date | undefined): number => {
    if (!dateValue) return 0;
    if (dateValue instanceof Date) return dateValue.getTime();
    if (typeof dateValue === 'string') {
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return 0;
        return date.getTime();
    }
    return 0;
};

interface MessageListProps {
    messages: Message[];
    currentUserId: string;
    onAddReaction: (messageId: number, reactionType: string) => void;
    externalUserDetails?: ValidateOtpResponseDto | null;
    scrollToBottomTrigger?: boolean;
    onLoadMore?: () => Promise<number>;
    loadingTop?: boolean;
    hasMore?: boolean;
}

const MessageList: React.FC<MessageListProps> = ({ 
    messages, 
    currentUserId, 
    onAddReaction, 
    externalUserDetails, 
    scrollToBottomTrigger, 
    onLoadMore, 
    loadingTop = false,
    hasMore = true 
}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [toggledReactionMenuId, setToggledReactionMenuId] = useState<number | null>(null);
    const [visibleDateMessageIds, setVisibleDateMessageIds] = useState<number[]>([]);

    const isLoadingMoreRef = useRef(false);
    const prevFirstMsgIdRef = useRef<number | null>(null);

    // 1. PRE-PROCESS MESSAGES: Filter out duplicate call events and build groupings
    const { displayMessages, callGroups } = useMemo(() => {
        const groups: Record<number, Message[]> = {};
        const display: Message[] = [];
        const seenCallIds = new Set<number>();
        
        // Ensure chronological order first
        const sorted = [...messages].sort((a, b) => getUTCTime(a.createDate) - getUTCTime(b.createDate));

        sorted.forEach(msg => {
            // Check if this is a system message related to a call
            if (msg.messageType === 'SYSTEM' && msg.callRecordId && msg.callRecordId !== 0) {
                // Add to the group bucket
                if (!groups[msg.callRecordId]) {
                    groups[msg.callRecordId] = [];
                }
                groups[msg.callRecordId].push(msg);

                // Add to main display list ONLY if it's the first time we see this Call ID
                if (!seenCallIds.has(msg.callRecordId)) {
                    seenCallIds.add(msg.callRecordId);
                    display.push(msg);
                }
                // If we HAVE seen it, we skip pushing it to 'display', effectively hiding it from the timeline
            } else {
                // Normal user messages or non-call system messages always show
                display.push(msg);
            }
        });

        return { displayMessages: display, callGroups: groups };
    }, [messages]);

    // 2. GROUP BY DATE: Use the filtered list
    const groupedMessages = useMemo(() => {
        const groups = groupMessagesByDate(displayMessages);
        // Sort individual date groups just in case
        groups.forEach(group => {
            group.messages.sort((a, b) => getUTCTime(a.createDate) - getUTCTime(b.createDate));
        });
        return groups;
    }, [displayMessages]);

    const handleToggleDateVisibility = (messageId: number) => {
        setVisibleDateMessageIds(prevIds =>
            prevIds.includes(messageId) ? prevIds.filter(id => id !== messageId) : [...prevIds, messageId]
        );
    };

    const handleToggleReactionMenu = (messageId: number) => {
        setToggledReactionMenuId(prevId => (prevId === messageId ? null : messageId));
    };

    const handleAddReactionAndClose = (messageId: number, reactionType: string) => {
        onAddReaction(messageId, reactionType);
        setToggledReactionMenuId(null);
    };

    // Scroll Logic
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;
        const firstMsgId = messages.length > 0 ? messages[0].id : null;
        const prevFirst = prevFirstMsgIdRef.current;

        if (prevFirst === null) {
            setTimeout(() => { container.scrollTop = container.scrollHeight; }, 50);
        } else if (firstMsgId !== null && prevFirst !== null && firstMsgId >= prevFirst) {
             setTimeout(() => { container.scrollTop = container.scrollHeight; }, 50);
        }
        prevFirstMsgIdRef.current = firstMsgId;
    }, [messages.length, scrollToBottomTrigger]); // Changed dependency to messages.length to catch updates

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container || !onLoadMore) return;
        let ticking = false;
        const handleScroll = async () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(async () => {
                try {
                    if (container.scrollTop <= 80 && hasMore && !loadingTop && !isLoadingMoreRef.current) {
                        const prevScrollHeight = container.scrollHeight;
                        isLoadingMoreRef.current = true;
                        await onLoadMore();
                        setTimeout(() => {
                            container.scrollTop = container.scrollHeight - prevScrollHeight;
                            isLoadingMoreRef.current = false;
                        }, 50);
                    }
                } finally { ticking = false; }
            });
        };
        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
    }, [onLoadMore, hasMore, loadingTop]);

    return (
        <div className="chat_body_inner" ref={scrollContainerRef}>
            {loadingTop && (
                <div className="w-full flex items-center justify-center p-2">
                    <div className="loader-small">Loading...</div>
                </div>
            )}
            
            {groupedMessages.map((group, index) => (
                <div key={group.dateLabel || index} className="chat_flx_message">
                    <div className="date_header"><span>{group.dateLabel}</span></div>
                    
                    {group.messages.map((msg) => {
                        // RENDER LOGIC:
                        // If it's a Call System message, it MUST be the "first" one (since we filtered others).
                        // We render the CallLogMessage component and pass IT the full bucket of events.
                        if (msg.messageType === 'SYSTEM' && msg.callRecordId && callGroups[msg.callRecordId]) {
                            const fullCallHistory = callGroups[msg.callRecordId];
                            return (
                                <CallLogMessage
                                    key={`call-group-${msg.callRecordId}`} 
                                    messages={fullCallHistory} 
                                />
                            );
                        }

                        // Otherwise standard message
                        return (
                            <MessageItem
                                key={msg.id}
                                message={msg}
                                toggledReactionMenuId={toggledReactionMenuId}
                                onToggleReactionMenu={handleToggleReactionMenu}
                                onAddReaction={handleAddReactionAndClose}
                                isDateVisible={visibleDateMessageIds.includes(msg.id)}
                                onToggleDate={handleToggleDateVisibility}
                                externalUserDetails={externalUserDetails}
                            />
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

export default MessageList;